import React from "react";
import * as Ariakit from "@ariakit/react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import RicercaPerCorso from "./RicercaPerCorso";
import Title from "../title";
import ResetCss from "../ResetCss";

import "./Tab.css";
import RicercaPerDipendente from "./RicercaPerDipendente";
import RicercaPerScadenza from "./RicercaPerScadenza";
import PrenotaCorsoSede from "./PrenotaCorsoSede";
import {
  CertificatesDatasetProvider,
  useCertificatesDataset,
} from "../../features/certificate/useCertificatesDataset";
import { AvvisoListaIncompleta } from "./avviso-lista-incompleta";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import Suggerimento from "../tooltip/suggerimento";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 4rem;
  filter: drop-shadow(0px -4px 5px var(--shadow500));
  margin-top: 2rem;
`;

const TabContentContainer = styled.div`
  padding: 1em 2em;
  width: "1300px";
  background-color: var(--white);
  border-radius: 1rem;
  min-height: 300px;
`;

// L'avviso vive dentro il provider perché il flag arriva col set attestati.
// Finché il set non è arrivato non c'è nulla da dichiarare: il caso normale è
// una lista completa e la barra non compare.
function AvvisoSeTroncata() {
  const { troncata } = useCertificatesDataset();
  if (!troncata) return null;
  return <AvvisoListaIncompleta />;
}

export default function AttestatiPage() {
  const { t } = useTranslation();
  const [urlParams] = useSearchParams();
  const activeTab = urlParams.get("tab") || "corso";

  const tab = Ariakit.useTabStore({ defaultSelectedId: activeTab });
  const selectedId = Ariakit.useStoreState(tab, "selectedId");

  // Un nuovo indirizzo con `?tab=` (click su un KPI dentro la rotta) porta
  // sulla tab indicata senza ricaricare la pagina.
  React.useEffect(() => {
    tab.setSelectedId(activeTab);
  }, [tab, activeTab]);

  // Montaggio lazy delle tab: ogni pannello viene renderizzato solo la prima
  // volta che diventa attivo e poi resta montato. Evita che all'apertura della
  // pagina partano in parallelo le chiamate di tutte le ricerche quando se ne
  // visualizza una sola, senza rifarle a ogni cambio tab.
  const [visitedTabs, setVisitedTabs] = React.useState<Set<string>>(
    () => new Set([activeTab])
  );
  React.useEffect(() => {
    if (!selectedId) return;
    setVisitedTabs((prev) => {
      if (prev.has(selectedId)) return prev;
      const next = new Set(prev);
      next.add(selectedId);
      return next;
    });
  }, [selectedId]);

  // Il set attestati (lista, sedi, catalogo) è condiviso fra le tab e
  // recuperato una volta per visita alla rotta.
  return (
    <CertificatesDatasetProvider>
      {/* Gli avvisi delle tab (richiesta di un corso dedicato, prenotazione senza
          partecipanti) hanno bisogno di un contenitore montato nella pagina. */}
      <ToastContainer />
      <ResetCss>
        <Title text="Gestione corsi" />
        <Layout>
          <AvvisoSeTroncata />
          <Ariakit.TabProvider store={tab}>
            <Ariakit.TabList className="tab-list" aria-label="corsi">
              <Ariakit.Tab className="tab" id={"corso"}>
                CERCA PER CORSO
              </Ariakit.Tab>
              <Ariakit.Tab className="tab" id={"dipendente"}>
                CERCA PER DIPENDENTE
              </Ariakit.Tab>
              <Ariakit.Tab className="tab" id={"scadenza"}>
                CERCA PER SCADENZA
              </Ariakit.Tab>
              <Suggerimento testo={t("attestati-aiuto-tab-corso-dedicato")}>
                <Ariakit.Tab className="tab" id={"prenota"}>
                  RICHIEDI UN CORSO DEDICATO
                </Ariakit.Tab>
              </Suggerimento>
            </Ariakit.TabList>
            <TabContentContainer>
              <Ariakit.TabPanel tabId={"corso"}>
                {visitedTabs.has("corso") && <RicercaPerCorso />}
              </Ariakit.TabPanel>
              <Ariakit.TabPanel tabId={"dipendente"}>
                {visitedTabs.has("dipendente") && <RicercaPerDipendente />}
              </Ariakit.TabPanel>
              <Ariakit.TabPanel tabId={"scadenza"}>
                {visitedTabs.has("scadenza") && <RicercaPerScadenza />}
              </Ariakit.TabPanel>
              <Ariakit.TabPanel tabId={"prenota"}>
                {visitedTabs.has("prenota") && <PrenotaCorsoSede />}
              </Ariakit.TabPanel>
            </TabContentContainer>
          </Ariakit.TabProvider>
        </Layout>
      </ResetCss>
    </CertificatesDatasetProvider>
  );
}
